import React, { useRef, useEffect } from 'react';
import Card from 'src/components/common/Card.redesign';
import { TweenLite } from 'gsap';
import Moment from 'moment';
import TractHorizonSummary, { HorizonSummary } from 'src/models/tractHorizonSummary';
import { ContextMenuAction } from 'src/components/common/ContextMenu';
import MathUtil from 'src/util/MathUtil';
import tagsToString from 'src/components/common/UtilityFunctions';

interface ITractCardProps {
    key?: string;
    actions?: ContextMenuAction[];
    tract: TractHorizonSummary;
    onClick: (event: React.MouseEvent<HTMLDivElement>, horizonNumber: number) => void;
}

const TractCard = (props: ITractCardProps) => {
    const legDescEl = useRef(null);

    useEffect(() => {
        TweenLite.to(legDescEl.current, 0, { height: '2.5rem', background: 'transparent', borderColor: 'transparent', zIndex: 0 });
    });

    const toggleLegalDesc = (state: boolean) => {
        if (state) {
            TweenLite.to(legDescEl.current, 0.2, { height: 'auto', background: 'white', borderColor: '#ccc', zIndex: 10 });
        } else {
            TweenLite.to(legDescEl.current, 0.2, { height: '2.5rem', background: 'transparent', borderColor: 'transparent', zIndex: 0 });
        }
    };

    const badgeInfo = (
        <span style={{ fontSize: '.9rem', position: 'relative', marginTop: '.2rem', display: 'block' }}>
            <span style={{ fontWeight: 600 }}>Modified: </span>
            {Moment(props.tract.lastModified).format('MM/DD/YYYY')}
            <span style={{ paddingLeft: '1rem', fontWeight: 600 }}>Plotted: </span>
            {props.tract.isPlotted ? '✅' : '❌'}
        </span>
    );

    const tractCell = (label: string, data: string | number, isBlank = false) => {
        if (isBlank) {
            return <span style={{ display: 'table-cell', width: '25%' }} />;
        }

        return (
            <span style={{ display: 'table-cell', width: '25%' }}>
                <span
                    style={{
                        width: '50%',
                        background: '#ccc',
                        color: '#555',
                        fontSize: '.9em',
                        fontWeight: 'bold',
                        display: 'inline-block',
                        padding: '.2rem 0 .2rem 1rem',
                    }}
                >
                    {label}
                </span>
                <span
                    style={{
                        width: '50%',
                        textAlign: 'right',
                        color: '#6393c1',
                        fontWeight: 'bold',
                        display: 'inline-block',
                        fontSize: '.75em',
                        padding: '.2rem 1rem .2rem 0',
                    }}
                >
                    {MathUtil.cleanNumber((data as number))}
                </span>
            </span>
        );
    };

    const acreagePanel = (horizon: HorizonSummary) => (
        <div style={{ display: 'table', width: '100%', justifyContent: 'space-between', border: '1px solid #ccc' }}>
            {tractCell('Gross', horizon.grossAcres)}
            {tractCell('HBP', horizon.hbpAcres)}
            {tractCell('Open', horizon.openAcres)}
            {tractCell('Leased', horizon.leasedAcres)}
        </div>
    );

    const handleHorizonClick = (event: React.MouseEvent<HTMLDivElement>, horizon: HorizonSummary) => {
        event.stopPropagation();
        props.onClick(event, horizon.horizonNumber);
    };

    const horizonPanel = (horizon: HorizonSummary) => {
        const style: React.CSSProperties = {
            borderTop: '1px solid #ccc',
        };

        const badgeStyle: React.CSSProperties = {
            fontSize: '.8em',
            padding: '.1rem .5rem',
            background: '#00BF00',
            border: '1px solid #008000',
            marginLeft: '.5rem',
            borderRadius: '.3rem',
            marginRight: '.5rem',
            position: 'relative',
            fontWeight: 'bold',
            color: 'white',
        };

        let th = <></>;

        if (horizon.isTargetHorizon) {
            th = <span style={badgeStyle}>Target Horizon</span>;
        }

        return (
            <div key={horizon.id} style={style} onClick={(e) => handleHorizonClick(e, horizon)}>
                <div className="card-inner-hoverable-panel">
                    <div style={{ paddingBottom: '0.5rem' }}>
                        <b>Horizon {horizon.horizonNumber}:</b> {th}
                        {horizon.description}
                    </div>
                    <div style={{ paddingBottom: '0.5rem', position: 'absolute', marginRight: '1rem', marginTop: '-2.3rem', right: '0' }}>
                        Owners: {horizon.ownerCount}
                    </div>
                    {acreagePanel(horizon)}
                </div>
            </div>
        );
    };

    const renderHorizons = (horizons: HorizonSummary[]) => {
        const badgeStyle: React.CSSProperties = {
            fontSize: '.8em',
            padding: '.1rem .5rem',
            background: '#00BF00',
            border: '1px solid #008000',
            marginLeft: '.5rem',
            borderRadius: '.3rem',
            marginRight: '.5rem',
            position: 'relative',
            fontWeight: 'bold',
            color: 'white',
        };

        if (!horizons) {
            return <div>No Horizons on Tract</div>;
        } else if (horizons.length === 1) {
            let th = <></>;

            if (horizons[0].isTargetHorizon) {
                th = <span style={badgeStyle}>Target Horizon</span>;
            }

            return (
                <div style={{ padding: '1rem', paddingTop: '0' }}>
                    <div style={{ paddingBottom: '0.5rem' }}>
                        <b>Horizon {horizons[0].horizonNumber}</b>: {th}
                        {horizons[0].description}
                    </div>
                    <div style={{ paddingBottom: '0.5rem', position: 'absolute', marginRight: '1rem', marginTop: '-2.3rem', right: '0' }}>
                        Owners: {horizons[0].ownerCount}
                    </div>
                    {acreagePanel(horizons[0])}
                </div>
            );
        } else {
            return horizons.map((h: HorizonSummary) => {
                return horizonPanel(h);
            });
        }
    };

    return (
        <Card
            onClick={(e) => props.onClick(e, props.tract.horizons[0].horizonNumber)}
            badge={`${props.tract.tractNumber}`}
            rightBadgePanel={badgeInfo}
            contextActions={props.actions}
            newContext={true}
            showMenu={true}
        >
            <div>
                <div style={{ width: '100%', padding: '1rem 1rem' }}>
                    <span 
                        style={{ visibility: (props.tract.tags?.length ? props.tract.tags?.length : 0) < 1 ? 'hidden' : 'visible',
                         fontWeight: 'bold', display: 'inline-block', marginRight: '.6rem', whiteSpace: 'nowrap' }}
                    >Tags:
                    </span>
                    <span>
                        {tagsToString(props.tract?.tags)}
                    </span>
                </div>
                <div style={{ width: '100%', padding: '1rem 1rem' }}>
                    <span
                        style={{
                            maxHeight: '2rem',
                            width: '65%',
                            position: 'relative',
                            top: '.5rem',
                            display: 'inline-flex',
                        }}
                    >
                        <span style={{ fontWeight: 'bold', display: 'inline-block', marginRight: '.6rem', whiteSpace: 'nowrap' }}>Tract Name:</span>
                        <span
                            style={{
                                display: 'inline-block',
                                textOverflow: 'ellipsis',
                                overflow: 'hidden',
                                position: 'absolute',
                                border: '1px solid transparent',
                                padding: '.3rem',
                                top: '-0.5rem',
                                marginLeft: '15rem',
                                zIndex: 0,
                                height: 'auto',
                            }}
                        >
                            {props.tract.tractName}
                        </span>
                    </span>
                </div>
                <div style={{ width: '100%', padding: '1rem 1rem' }}>
                    <span
                        style={{
                            maxHeight: '2rem',
                            width: '65%',
                            position: 'relative',
                            top: '.5rem',
                            display: 'inline-flex',
                        }}
                    >
                        <span
                            onMouseOver={() => toggleLegalDesc(true)}
                            onMouseOut={() => toggleLegalDesc(false)}
                            style={{ fontWeight: 'bold', display: 'inline-block', marginRight: '.6rem', whiteSpace: 'nowrap' }}
                        >
                            <span>👁️</span> Legal Description:
                        </span>
                        <span
                            ref={legDescEl}
                            style={{
                                display: 'inline-block',
                                textOverflow: 'ellipsis',
                                overflow: 'hidden',
                                position: 'absolute',
                                border: '1px solid transparent',
                                padding: '.3rem',
                                top: '-0.5rem',
                                marginLeft: '15rem',
                                background: 'white',
                                zIndex: 0,
                                height: 'auto',
                            }}
                        >
                            {props.tract.legalDescription}
                        </span>
                    </span>
                </div>
                {renderHorizons(props.tract.horizons)}
                {/* <div style={{ display: 'table', width: '100%', justifyContent: 'space-between', borderTop: '1px solid #ccc', marginTop: '.75rem' }}>
                    {tractCell('Ownership', props.tract.maxOwnershipInterest)}
                    {tractCell('Owners', props.tract.ownerCount)}
                    {tractCell('Horizons', props.tract.horizonCount)}
                    {tractCell('', '', true)}
                </div>
                <div style={{ display: 'table', width: '100%', justifyContent: 'space-between', borderTop: '1px solid #ccc', borderBottom: '1px solid #ccc' }}>
                    {tractCell('Gross', props.tract.grossAcres)}
                    {tractCell('HBP', props.tract.hbpAcres)}
                    {tractCell('Open', (Number(props.tract.grossAcres) - Number(props.tract.leasedAcres)))}
                    {tractCell('Leased', props.tract.leasedAcres)}
                </div> */}
            </div>
        </Card>
    );
};

export default TractCard;



// WEBPACK FOOTER //
// ./src/components/project/sections/TractComponents/TractCard2.tsx